import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EnvironmentValue } from '@majuscule/contracts';
import { useState } from 'react';
import { useToast } from './toast';
import { internalFetch, internalSend } from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHead } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { KvList, KvRow } from '@/components/ui/kv';

const TITLES: Record<EnvironmentValue, { title: string; description: string }> = {
  production: {
    title: 'Webhook',
    description: "Notifications de fin de requête envoyées à l'ERP.",
  },
  staging: {
    title: 'Webhook Staging',
    description: "Notifications des requêtes faites avec une clé Staging, envoyées à l'ERP de recette.",
  },
};

/** Webhook of one environment of a client: current URL and signing secret, URL replacement. */
export function ClientWebhook({
  clientId,
  environment,
}: {
  clientId: string;
  environment: EnvironmentValue;
}): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();
  const path = `/internal/clients/${clientId}/webhook?environment=${environment}`;

  const webhook = useQuery({
    queryKey: ['webhook', clientId, environment],
    queryFn: () => internalFetch<{ url: string | null; secret: string | null }>(path),
  });

  const [webhookUrl, setWebhookUrl] = useState('');
  const setWebhook = useMutation({
    mutationFn: () => internalSend('PUT', path, { url: webhookUrl }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['webhook', clientId, environment] });
      notify('Webhook enregistré');
    },
  });

  const secret = webhook.data?.secret ?? null;

  return (
    <Card>
      <CardHead
        title={TITLES[environment].title}
        description={TITLES[environment].description}
      />
      <CardContent className="grid gap-4">
        <KvList>
          <KvRow label="URL actuelle" mono>
            <span className="[overflow-wrap:anywhere]">{webhook.data?.url ?? '—'}</span>
          </KvRow>
          {secret !== null ? (
            <KvRow label="Secret" mono>
              <span className="[overflow-wrap:anywhere]">{secret}</span>
            </KvRow>
          ) : null}
        </KvList>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setWebhook.mutate();
          }}
        >
          <Field label="Nouvelle URL" className="min-w-0 flex-1 basis-64">
            <Input
              className="font-mono"
              value={webhookUrl}
              onChange={(event) => {
                setWebhookUrl(event.target.value);
              }}
              placeholder="https://erp.example.com/webhooks/majuscule"
            />
          </Field>
          <Button type="submit" disabled={setWebhook.isPending}>
            Enregistrer
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
